import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  Button,
  Collapse,
  Divider,
  Stack,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAppConfig } from "../../../context/AppConfigContext";
import { useWeddingWizard } from "../../../context/WeddingWizardContext";
import { useWeddingComposites } from "../../../hooks/useWeddingComposites";

function findPackageForGuests(packages, guests) {
  const g = Number(guests) || 0;
  return (packages || []).find(
    (p) => g >= (p.min ?? 0) && (p.max == null || g <= p.max)
  );
}

// Prefer DB (WeddingComposite) and fall back to legacy AppConfig
function useCompositeIndex(config, dbComposites) {
  return useMemo(() => {
    const index = new Map();

    if (Array.isArray(dbComposites) && dbComposites.length) {
      dbComposites.forEach((c) => {
        const entry = {
          kind: "wedding",
          key: c.key,
          label: c.label,
          variant: c.variant, // "standard" | "tall"
          tierCount:
            Number(c.tierCount) ||
            (Array.isArray(c.tiers) ? c.tiers.length : 0),
          tiers: Array.isArray(c.tiers) ? c.tiers : [],
          servings: Number(c.totalServings) || 0,
          overhead: Number(c.overhead) || 0,
          notes: c.notes || "",
          media: c.media || null,
        };
        index.set(c.key, entry);
        (c.aliases || []).forEach((a) => index.set(a, entry));
      });
    }

    const legacy = config?.weddings?.composites || {};
    Object.entries(legacy).forEach(([key, comp]) => {
      if (!index.has(key)) {
        index.set(key, {
          kind: "legacy",
          key,
          label: comp.label || key,
          variant: undefined,
          tierCount: Array.isArray(comp.sizeNames) ? comp.sizeNames.length : 0,
          tiers: (comp.sizeNames || []).map((name) => ({
            displayName: name,
            diameterIn: null,
            heightIn: null,
            shape: "round",
          })),
          media: comp.media,
          servings: Number(comp.servings) || 0,
          overhead: Number(comp.overhead) || 0,
          notes: "",
        });
      }
    });

    return index;
  }, [config, dbComposites]);
}

// Build concise tier bullets from smallest→largest input (display bottom→top)
function TierBullets({ comp }) {
  const tiers = Array.isArray(comp.tiers) ? comp.tiers : [];
  if (!tiers.length) return null;

  const ordered = [...tiers].sort(
    (a, b) => (b.diameterIn || 0) - (a.diameterIn || 0)
  );
  const labels = (len) =>
    len === 1
      ? ["Single tier"]
      : len === 2
      ? ["Bottom tier", "Top tier"]
      : ["Bottom tier", "Middle tier", "Top tier"];

  const names = labels(ordered.length);

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {ordered.map((t, i) => (
        <Typography key={`${comp.key}-tierline-${i}`} variant="body2">
          • {names[i]}: {t.diameterIn ?? "?"}″ (W) × {t.heightIn ?? "?"}″ (H)
        </Typography>
      ))}
    </Stack>
  );
}

function positionLabel(i, count) {
  if (count === 1) return "single";
  if (i === 0) return "bottom";
  if (i === count - 1) return "top";
  return "middle";
}

export default function ConfigSuggestionsStep() {
  const { config } = useAppConfig();
  const { composites: dbComposites, loading } = useWeddingComposites();
  const {
    state,
    setCakeInclude,
    setCompositeKey,
    setCakeTiers,
    setDessertsInclude,
    next,
  } = useWeddingWizard();

  // pull from planner hint, not state.guests
  const guests = Number(state?.planner?.guestsForSizing) || 0;

  const { options } = useMemo(() => {
    const p = findPackageForGuests(config?.weddings?.packages || [], guests);
    return { options: p?.options || [] };
  }, [config, guests]);

  const compositeIndex = useCompositeIndex(config, dbComposites);
  const [expandedKey, setExpandedKey] = useState(null);

  const handlePick = (compositeKey) => {
    const comp = compositeIndex.get(compositeKey);
    const opt = (options || []).find((o) => o.compositeKey === compositeKey);

    // ensure cake is included
    if (!state.cake?.include) setCakeInclude(true);

    // set composite
    setCompositeKey(compositeKey);

    // prime tiers array (largest → smallest as bottom→top)
    const tierCount =
      comp?.tierCount ||
      (Array.isArray(comp?.tiers) ? comp.tiers.length : 0) ||
      0;

    if (tierCount > 0) {
      const sorted = Array.isArray(comp?.tiers)
        ? [...comp.tiers].sort(
            (a, b) => (b.diameterIn || 0) - (a.diameterIn || 0)
          )
        : Array.from({ length: tierCount }, () => ({}));

      // build placeholder tiers with labeled positions and empty flavor
      const tiers = sorted.map((_, i) => ({
        position: positionLabel(i, sorted.length), // 'bottom' | 'middle' | 'top' | 'single'
        flavorId: "",
      }));
      setCakeTiers(tiers);
    } else {
      setCakeTiers([]);
    }

    // dessert hint → toggle include
    const optMeta = (options || []).find(
      (o) => o.compositeKey === compositeKey
    );
    setDessertsInclude(!!optMeta?.dessertsHint);

    next();
  };

  if ((loading && !options.length) || !options.length) {
    return (
      <Typography variant="body1">
        {loading ? "Loading…" : "No presets for your guest count yet."}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, mt: 2, fontWeight: 800 }}>
        Recommended for {guests} people
      </Typography>

      <Grid container spacing={2}>
        {options.map((opt) => {
          const comp = compositeIndex.get(opt.compositeKey);
          if (!comp) return null;
          const isOpen = expandedKey === opt.compositeKey;

          // NEW: prefer package coverImage; keep old opt.image as a fallback for compatibility
          const img =
            opt.coverImage ||
            opt.image ||
            comp?.media?.hero ||
            comp?.media?.thumb ||
            (Array.isArray(comp?.media?.gallery) && comp.media.gallery[0]) ||
            null;

          const alt =
            (opt.coverImage && `${opt.title} (package cover)`) ||
            comp?.media?.alt ||
            `${opt.title} (reference image)`;

          return (
            <Grid item xs={12} md={6} key={`${opt.title}-${opt.compositeKey}`}>
              <Card
                variant="outlined"
                sx={{ borderRadius: 3, overflow: "hidden" }}>
                <CardActionArea
                  onClick={() => handlePick(opt.compositeKey)}
                  sx={{
                    textAlign: "left",
                    "&:hover .image-swatch": { opacity: 0.95 },
                  }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      {opt.title}
                    </Typography>

                    {/* IMAGE + BADGE */}
                    <Box
                      className="image-swatch"
                      sx={{
                        mt: 1.25,
                        borderRadius: 2,
                        height: 400,
                        bgcolor: "rgba(0,0,0,0.04)",
                        overflow: "hidden",
                        position: "relative",
                      }}>
                      {img ? (
                        <Box
                          component="img"
                          src={img}
                          alt={alt}
                          onError={(e) => {
                            e.currentTarget.src = "/placeholder.png";
                          }}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      ) : null}

                      {/* Top-left badge */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: "rgba(0,0,0,0.6)",
                          color: "white",
                          fontSize: 12,
                          fontWeight: 600,
                          letterSpacing: 0.2,
                        }}>
                        Image for reference only
                      </Box>
                    </Box>

                    {/* REFERENCE CAPTION */}
                    {/* <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.75 }}>
                      Cover images are for reference only. You'll choose the
                      cake style in the next step.
                    </Typography> */}

                    <TierBullets comp={comp} />
                  </CardContent>
                </CardActionArea>

                {/* Details toggle (does NOT select) */}
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation(); // prevent selecting when toggling
                      setExpandedKey(isOpen ? null : opt.compositeKey);
                    }}
                    startIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 150ms ease",
                        }}
                      />
                    }
                    sx={{
                      px: 0,
                      minWidth: 0,
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                    }}>
                    {isOpen ? "Hide details" : "See details"}
                  </Button>

                  <Collapse in={isOpen} unmountOnExit>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 1 }}>
                      Servings use wedding slices. “Standard” tiers are ~4″
                      high; “Tall” tiers are ~6″ high.
                    </Typography>
                  </Collapse>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
